// ============================================================================
// DEMO TEMPLATE CATALOG PAGE (Admin)
// Browse and manage the global demonstrator template catalog.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Rocket } from 'lucide-react';
import { listDemoTemplates, deleteDemoTemplate, DemoTemplate } from '../services/demoTemplatesService';
import { EmptyState } from '../components/EmptyState';
import { ConfirmModal } from '../components/common/ConfirmModal';

type CategoryFilter = 'all' | DemoTemplate['category'];
type ActiveFilter = 'all' | 'active' | 'inactive';

const CATEGORY_LABELS: Record<DemoTemplate['category'], string> = {
  'agentic-workflow': 'Agentic Workflow',
  'customer-portal': 'Customer Portal',
  'report': 'Report',
  'other': 'Other',
};

const CATEGORY_FILTER_OPTIONS: { value: CategoryFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'agentic-workflow', label: 'Agentic Workflow' },
  { value: 'customer-portal', label: 'Customer Portal' },
  { value: 'report', label: 'Report' },
  { value: 'other', label: 'Other' },
];

export default function DemoTemplateCatalog() {
  const navigate = useNavigate();

  const [templates, setTemplates] = useState<DemoTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');
  const [deleteTarget, setDeleteTarget] = useState<DemoTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await listDemoTemplates();
      setTemplates(result);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load demonstrator templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await deleteDemoTemplate(deleteTarget.templateId);
      setDeleteTarget(null);
      fetchTemplates();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  const filtered = templates.filter((t) => {
    if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
    if (activeFilter === 'active' && !t.active) return false;
    if (activeFilter === 'inactive' && t.active) return false;
    return true;
  });

  let tableSection: React.ReactNode;
  if (loading) {
    tableSection = (
      <div className="flex justify-center items-center py-20 text-[#64748b]">
        <div className="text-sm font-medium">Loading demonstrator templates...</div>
      </div>
    );
  } else if (error) {
    tableSection = (
      <div className="p-6 text-center">
        <p className="text-sm text-red-700">Failed to load templates: {error}</p>
      </div>
    );
  } else if (filtered.length === 0) {
    tableSection = (
      <EmptyState
        icon={Rocket}
        title={templates.length === 0 ? 'No demonstrator templates yet' : 'No templates match these filters'}
        description={templates.length === 0 ? 'Create your first template to get started' : 'Try a different filter'}
        action={
          templates.length === 0 ? (
            <button
              onClick={() => navigate('/admin/demo-templates/create')}
              className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-[#052831] font-bold rounded-xl transition-all shadow-md shadow-primary/20 text-sm"
            >
              Create Template
            </button>
          ) : undefined
        }
      />
    );
  } else {
    tableSection = (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Name</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Category</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Tech Stack</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Status</th>
              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Updated</th>
              <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#e2e8f0]">
            {filtered.map((template) => (
              <tr
                key={template.templateId}
                onClick={() => navigate(`/admin/demo-templates/${template.templateId}/edit`)}
                className="cursor-pointer hover:bg-slate-50/50 transition-colors"
              >
                <td className="px-4 py-4 max-w-[300px]">
                  <div className="font-medium text-[#052831]">{template.name}</div>
                  <div className="text-xs text-[#64748b] truncate mt-0.5">{template.description}</div>
                </td>
                <td className="px-4 py-4">
                  <span className="inline-block px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700">
                    {CATEGORY_LABELS[template.category]}
                  </span>
                </td>
                <td className="px-4 py-4 text-xs text-[#64748b]">{template.techStack}</td>
                <td className="px-4 py-4">
                  <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold ${
                    template.active ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {template.active ? 'Active' : 'Draft (no zip)'}
                  </span>
                </td>
                <td className="px-4 py-4 text-xs text-slate-400">{new Date(template.updatedAt).toLocaleDateString()}</td>
                <td className="px-4 py-4">
                  <div className="flex gap-1.5 justify-end" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => navigate(`/admin/demo-templates/${template.templateId}/edit`)}
                      className="p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-all"
                      title="Edit"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(template)}
                      className="p-1.5 rounded-lg border border-red-200 text-red-400 hover:text-red-600 hover:border-red-300 transition-all"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Rocket size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Demonstrator Templates</h1>
              <p className="text-slate-500 text-sm font-medium">Manage the starter-repo catalog used by the session-to-demo build workflow</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin/demo-templates/create')}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
          >
            <Plus size={16} />
            Create Template
          </button>
        </header>

        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#e2e8f0]">
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
                {CATEGORY_FILTER_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setCategoryFilter(opt.value)}
                    className={`px-4 py-2 text-xs font-semibold transition-all ${
                      categoryFilter === opt.value ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value as ActiveFilter)}
                className="px-3 py-2.5 rounded-xl border border-[#e2e8f0] text-sm text-[#052831] focus:ring-2 focus:ring-primary focus:border-transparent outline-none cursor-pointer"
              >
                <option value="all">All statuses</option>
                <option value="active">Active only</option>
                <option value="inactive">Drafts only</option>
              </select>
            </div>
          </div>

          {tableSection}
        </div>

      </div>

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete demonstrator template"
        message={`Delete "${deleteTarget?.name}"? This removes its zip from storage and cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isProcessing={deleting}
      />
    </div>
  );
}
